import { FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
    autoSignIn,
    confirmSignIn,
    confirmSignUp,
    resendSignUpCode,
    type SignInOutput,
} from "aws-amplify/auth";
import {
    AuthError,
    AuthFooterLink,
    AuthHeading,
    BrandButton,
    BrandInput,
} from "../components/BrandControls.tsx";
import {
    AUTH_ROUTES,
    authErrorMessage,
    handleSignInNextStep,
    useResendCooldown,
    signInNeedsPassword,
} from "../authHelpers.ts";
import { rememberSignInWithoutPasskey } from "../passkeyNudge.ts";
import { requestEmailCode } from "../signInFlow.ts";
import { validateCode } from "../validation.ts";

/**
 * What the emailed code is for: signing in (the sign-in screen chose the
 * EMAIL_OTP factor, so Cognito sent it), or confirming the address of an
 * account that isn't confirmed yet (SignUp sent it, or a password sign-in
 * found the account unconfirmed).
 */
export type CodePurpose = "sign-in" | "sign-up";

/**
 * Sign in the account whose address a code just confirmed. Amplify does it
 * with the session ConfirmSignUp returned, but only for an account this tab
 * signed up less than three minutes ago: it drops the session after that, on a
 * reload, and never has one for an account found unconfirmed at sign-in. The
 * address is confirmed either way, so otherwise, or when that sign-in doesn't
 * finish, an email code gets them in, sent now rather than left for them to
 * ask for. Null locally, where cognito-local sends no codes and the sign-in
 * screen takes a password.
 */
async function signInConfirmedAccount(
    canAutoSignIn: boolean,
    email: string,
): Promise<SignInOutput | null> {
    if (canAutoSignIn) {
        try {
            const result = await autoSignIn();
            if (result.isSignedIn) {
                return result;
            }
        } catch (err) {
            console.error("Signing in after sign-up failed:", err);
        }
    }
    return signInNeedsPassword ? null : requestEmailCode(email);
}

/**
 * Confirm the code. Resolves to the sign-in it leads to, or null when there is
 * none to continue and the sign-in screen takes over.
 */
async function confirmCode(
    purpose: CodePurpose,
    email: string,
    code: string,
): Promise<SignInOutput | null> {
    if (purpose === "sign-in") {
        return confirmSignIn({ challengeResponse: code });
    }
    const { nextStep } = await confirmSignUp({
        username: email,
        confirmationCode: code,
    });
    return signInConfirmedAccount(
        nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN",
        email,
    );
}

/**
 * Ask for another code. Cognito has no resend for a sign-in code, so that is a
 * new sign-in session (see requestEmailCode); a sign-up code has its own
 * resend, and returns no sign-in.
 */
async function sendNewCode(
    purpose: CodePurpose,
    email: string,
): Promise<SignInOutput | null> {
    if (purpose === "sign-in") {
        return requestEmailCode(email);
    }
    await resendSignUpCode({ username: email });
    return null;
}

/**
 * The one screen that takes an emailed code, for signing in and for a new
 * account. Either way the same address that identifies the account receives
 * it, so no password is involved. A forgotten password is "Email me a code" on
 * the sign-in screen, which lands here too.
 */
export default function EmailCodeScreen({ purpose }: { purpose: CodePurpose }) {
    const navigate = useNavigate();
    const state = useLocation().state as {
        email?: string;
        confirmed?: boolean;
    } | null;
    const email = state?.email;
    const [code, setCode] = useState("");
    const [codeError, setCodeError] = useState<string>();
    const [formError, setFormError] = useState<string>();
    const [loading, setLoading] = useState(false);
    const { canResend, secondsRemaining, startCooldown } = useResendCooldown();

    // Reached only via a redirect that carries the email; guard direct hits.
    if (!email) {
        return <Navigate to={AUTH_ROUTES.signIn} replace />;
    }

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const err = validateCode(code);
        setCodeError(err);
        if (err) {
            return;
        }
        setLoading(true);
        setFormError(undefined);
        try {
            const result = await confirmCode(purpose, email, code.trim());
            if (!result) {
                navigate(AUTH_ROUTES.signIn);
                return;
            }
            if (
                purpose === "sign-up" &&
                result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE"
            ) {
                navigate(AUTH_ROUTES.emailCode, {
                    state: { email, confirmed: true },
                });
                return;
            }
            // Only when signing in. Someone who has just created an account
            // has enough in front of them; their next sign-in is where a
            // passkey saves them something, and that is when we ask.
            if (purpose === "sign-in") {
                rememberSignInWithoutPasskey(result, "code");
            }
            await handleSignInNextStep(result, email, navigate);
        } catch (err) {
            setFormError(authErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const onResend = async () => {
        setFormError(undefined);
        setCode("");
        try {
            const result = await sendNewCode(purpose, email);
            if (
                result &&
                result.nextStep.signInStep !== "CONFIRM_SIGN_IN_WITH_EMAIL_CODE"
            ) {
                await handleSignInNextStep(result, email, navigate);
                return;
            }
            startCooldown();
        } catch (err) {
            setFormError(authErrorMessage(err));
        }
    };

    const sentTo = <span className="text-brand-purple">{email}</span>;

    return (
        <>
            <AuthHeading
                title="Check your email"
                subtitle={
                    state?.confirmed ? (
                        <>
                            Your email is confirmed. Enter the sign-in code we
                            just sent to {sentTo}.
                        </>
                    ) : (
                        <>Enter the code we sent to {sentTo}.</>
                    )
                }
            />

            <form className="flex flex-col gap-4" onSubmit={onSubmit}>
                <AuthError message={formError} />
                <BrandInput
                    label="Code"
                    inputMode="numeric"
                    maxLength={8}
                    autoComplete="one-time-code"
                    placeholder="Enter your code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    error={codeError}
                    autoFocus
                />
                <BrandButton type="submit" loading={loading}>
                    {purpose === "sign-in" ? "Sign in" : "Verify email"}
                </BrandButton>
                <BrandButton
                    variant="outline"
                    type="button"
                    onClick={onResend}
                    disabled={!canResend}
                >
                    {canResend
                        ? "Send a new code"
                        : `Send a new code (${secondsRemaining}s)`}
                </BrandButton>
            </form>

            {purpose === "sign-in" ? (
                <AuthFooterLink
                    prompt="Wrong email?"
                    linkLabel="Back to sign in"
                    onClick={() => navigate(AUTH_ROUTES.signIn)}
                />
            ) : (
                <AuthFooterLink
                    prompt="Wrong email?"
                    linkLabel="Back to sign up"
                    onClick={() => navigate(AUTH_ROUTES.signUp)}
                />
            )}
        </>
    );
}
